// Globals
import { TFunction } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';

// Models
import { ROUTES } from '@/models/router.model';
import { IOrder, PartnerConfirmationStatus } from '@/models/orders.model';

// Components
import OrderStatusBadge from '../OrderStatusBadge/OrderStatusBadge';
import { Link } from '@/ui';
import { StyledConfirmationStatus, StyledOrderCount } from './OrdersListStyles';

// Helpers
import { formatUtmSource, getDeliveryTypeData, getPaymentChannelData } from '@/modules/Orders/helpers';
import { DateFormats } from '@/constants/dates';
import { assertUnreachable, formatDate } from '@/utils';

const getConfirmationLabel = (t: TFunction, status?: PartnerConfirmationStatus) => {
  switch (status) {
    case PartnerConfirmationStatus.CONFIRMED:
      return t('orders_partner_confirmation_confirmed');
    case PartnerConfirmationStatus.PARTIAL:
      return t('orders_partner_confirmation_partial');
    case PartnerConfirmationStatus.UNCONFIRMED:
    case undefined:
      return t('orders_partner_confirmation_not_confirmed');
    default:
      return assertUnreachable(status);
  }
};

export const getOrdersTableColumns = (
  t: TFunction,
  showManagerColumn = false,
  showConfirmationColumn = true,
): ColumnsType<IOrder> => {
  return [
    {
      title: t('orders_list_id'),
      dataIndex: 'number',
      className: 'id-column',
      key: 'number',
      render: (_, record) => <Link path={`${ROUTES.ORDERS}/${record.id}`}>{`${record.number}`}</Link>,
    },
    ...(showConfirmationColumn
      ? [
          {
            title: t('orders_list_partner_confirmation'),
            dataIndex: 'partner_confirmation_status',
            key: 'partner_confirmation_status',
            className: 'partner-confirmation-column',
            render: (_: unknown, record: IOrder) => {
              const status = record.partner_confirmation_status ?? PartnerConfirmationStatus.UNCONFIRMED;
              const confirmed = record.partner_confirmed_count ?? 0;
              const total = record.partner_confirmed_total ?? 0;

              return (
                <StyledConfirmationStatus $status={status}>
                  {getConfirmationLabel(t, status)}
                  {total > 0 ? ` ${confirmed}/${total}` : ''}
                </StyledConfirmationStatus>
              );
            },
          },
        ]
      : []),
    ...(showManagerColumn
      ? [
          {
            title: t('orders_list_manager'),
            dataIndex: 'manager',
            key: 'manager',
            render: (_: unknown, record: IOrder) => record.manager?.full_name ?? '',
          },
        ]
      : []),
    {
      title: t('orders_list_status'),
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        return <OrderStatusBadge t={t} record={record} />;
      },
    },
    {
      title: t('orders_list_contact_name'),
      dataIndex: 'contact_name',
      key: 'contact_name',
      render: (contactName, record) => (
        <>
          <div>{contactName}</div>
          <StyledOrderCount>{t('orders_list_orders_count', { count: record.orders_count ?? 0 })}</StyledOrderCount>
        </>
      ),
    },
    {
      title: t('orders_list_contact_phone'),
      dataIndex: 'contact_phone',
      key: 'contact_phone',
      render: (contact_phone) => contact_phone,
    },
    {
      title: t('orders_list_date'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at) => formatDate(created_at, DateFormats.EUROPEAN),
    },
    {
      title: t('orders_list_price'),
      dataIndex: 'total_price',
      key: 'total_price',
      render: (total_price) => total_price,
    },
    {
      title: t('orders_list_payment_channel'),
      dataIndex: 'payment_channel',
      key: 'payment_channel',
      render: (payment_channel) => {
        const labelKey = getPaymentChannelData(payment_channel);
        return labelKey ? t(labelKey) : '—';
      },
    },
    {
      title: t('orders_list_payment_status'),
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (_, record) => {
        return <OrderStatusBadge t={t} record={record} isPaymentStatus />;
      },
    },
    {
      title: t('orders_list_payment_date'),
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (payment_date: string | null | undefined) =>
        payment_date ? formatDate(payment_date, DateFormats.EUROPEAN) : '—',
    },
    {
      title: t('orders_list_delivery_type'),
      dataIndex: 'delivery_type',
      key: 'delivery_type',
      render: (delivery_type) => t(getDeliveryTypeData(delivery_type)),
    },
    {
      title: t('orders_list_source'),
      dataIndex: 'utm_source',
      key: 'utm_source',
      render: (utm_source: string | null | undefined) => formatUtmSource(t, utm_source) ?? '—',
    },
  ];
};
